import React from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { IndexRedirect, Route, Router } from 'react-router';
import * as url from 'url';
import * as _ from 'lodash';
import AppWrapper from './components/AppWrapper';
import Home from './components/user/home/Home';
import Login from './components/login/Login';
import Mail from './components/login/Mail';
import Forget from './components/login/Forget';
import Sure from './components/login/Sure';
import Register from './components/register/Register';
import RegisterGoogleCodeSelection from './components/register/RegisterGoogleCodeSelection';
import Classroom from './components/classroom/Classroom';
import CookieDetails from './components/common/cookieConsent/CookieDetails';
import AuthWrapper from './components/AuthWrapper';
import BookContent from './components/book/BookContent';
import * as SectionActions from './actions/sectionActions';
import * as AccountActions from './actions/accountActions';
import { setSignUpRole } from './actions/accountActions';
import * as jwtService from './services/jwt/jwtService';
import routerHistory from './routerHistory';
import { ROLE, ROUTES } from './const';
import ResetPassword from './components/login/ResetPassword';
import LibraryContainer from './components/library/LibraryContainer';
import ClassroomArchiveContainer from './components/user/home/archive/ClassroomArchiveContainer';
import DiscussionPage from './components/discussions/DiscussionPage/DiscussionPage';
import LaunchRedirect from './components/common/LaunchRedirect';
import {
  authTokenStorageHandler,
  getGuestTokenFromStorage,
  getTokenFromStorage,
  LOCAL_STORAGE,
  refreshTokenStorageHandler,
  removeLtiParamsFromStorage,
  setUrlToLogoutCallbackInStorage
} from './shared';
import { getParameterByName } from './utils/routeUtils';
import ErrorPage from './components/common/ErrorPage';
import { startConnection } from './services/socketConnection/socketSubscriberService';
import { startIpcMessages } from './electron/ipcNotifications';
import SearchResultsWrapper from './components/search-results/SearchResultsWrapper';
import { isElectronApp } from './services/crossPlatform/crossPlatformService';
import { removeQueryParam } from './utils/HttpUtils';
import RegisterAzureCodeSelection from './components/register/RegisterAzureCodeSelection';
import * as qs from 'qs';
import CloseWindowPage from './components/common/CloseWindowPage';
import { findClassroomRole } from './utils/classroomRoleUtils';
import GuestLanding from './components/guest/GuestLanding';
import GuestCatalogPage from './components/guest/GuestCatalogPage';

const customRedirect = path => {
  routerHistory.push(path);
};

const AUTH_TOKEN_PARAM = 'authToken';
const REFRESH_TOKEN_PARAM = 'refreshToken';
const URL_TO_LOGOUT_CALLBACK = 'logoutCallback';
const JAILED_PARAM = 'jailed';
const JAILED_TAB_PARAM = 'jailedTab';
const SSO_WITH_PARAM = 'ssoWith';
const JAILED_REGEXP = new RegExp('unitId=*|jailed=true');

export default class Routes extends React.Component {
  constructor(props) {
    super(props);
    this.retrieveParameters = this.retrieveParameters.bind(this);
    this.handleLTIParams = this.handleLTIParams.bind(this);
    this.requireAuth = this.requireAuth.bind(this);
    this.checkInstructorRole = this.checkInstructorRole.bind(this);
    this.checkStudentRole = this.checkStudentRole.bind(this);
    this.redirectIfLoggedIn = this.redirectIfLoggedIn.bind(this);
    this.setStudentRole = this.setStudentRole.bind(this);
    this.setInstructorRole = this.setInstructorRole.bind(this);
    this.checkEmailSentAndRedirectIfLoggedIn = this.checkEmailSentAndRedirectIfLoggedIn.bind(this);
    this.checkValidClassroomId = this.checkValidClassroomId.bind(this);
    startConnection(props.store);
    startIpcMessages(props.store);
  }

  static propTypes = {
    store: PropTypes.object
  };

  retrieveParameters(nextState, replace, next) {
    this.checkJailed(nextState);
    this.handleLTIParams(nextState);
    this.requireAuth(nextState, replace, next);
  }

  checkJailed(nextState) {
    const location = nextState.location;

    const jailed = JAILED_REGEXP.test(location.search) || JAILED_REGEXP.test(location.hash);
    const { dispatch } = this.props.store;
    if (jailed) {
      dispatch(AccountActions.setJailed(true));
      if ('ANALYTICS' === getParameterByName(JAILED_TAB_PARAM, location.search)) {
        const paramKeys = Object.keys(location.query);
        paramKeys.splice(paramKeys.indexOf(JAILED_PARAM), 1);
        paramKeys.splice(paramKeys.indexOf(JAILED_TAB_PARAM), 1);
        customRedirect(
          location.pathname + '/analytics?' + paramKeys.map(key => `${key}=${location.query[key]}`).join('&')
        );
      }
    }
  }

  handleLTIParams(nextState) {
    localStorage.removeItem(LOCAL_STORAGE.LIS_RESOURCE_ID);
    let location = nextState.location;
    if (location.search) {
      let outcomeServiceUrl = getParameterByName(LOCAL_STORAGE.LIS_OUTCOME_SERVICE_URL, location.search);
      let resultSourceId = getParameterByName(LOCAL_STORAGE.LIS_RESULT_SOURCED_ID, location.search);
      let resourceId = getParameterByName(LOCAL_STORAGE.LIS_RESOURCE_ID, location.search);
      let userIds = getParameterByName(LOCAL_STORAGE.LIS_USER_IDS, location.search);

      if (!_.isNil(outcomeServiceUrl)) {
        localStorage.setItem(LOCAL_STORAGE.LIS_OUTCOME_SERVICE_URL, outcomeServiceUrl);
      }

      if (!_.isNil(resultSourceId)) {
        localStorage.setItem(LOCAL_STORAGE.LIS_RESULT_SOURCED_ID, resultSourceId);
      }

      if (!_.isNil(resourceId)) {
        localStorage.setItem(LOCAL_STORAGE.LIS_RESOURCE_ID, resourceId);
      }

      if (!_.isNil(userIds)) {
        localStorage.setItem(LOCAL_STORAGE.LIS_USER_IDS, userIds);
      }
    }

    // Entrada sin lanzamiento LTI desde Moodle: limpiar params residuales para no reutilizar
    // un contexto de otra actividad al navegar dentro de la clase.
    if (_.isNil(localStorage.getItem(LOCAL_STORAGE.LIS_RESOURCE_ID))) {
      removeLtiParamsFromStorage();
    }
  }

  async requireAuth(nextState, replace, next) {
    const ssoQueryParam = nextState.location.query['ssoWith'];
    let location = nextState.location;
    const redirectAfterLogin = url.format({
      pathname: nextState.location.pathname,
      search: nextState.location.search,
      hash: nextState.location.hash
    });
    const token = getTokenFromStorage();
    const { dispatch } = this.props.store;
    if (ssoQueryParam && ssoQueryParam === 'google' && (!token || jwtService.hasExpired(token))) {
      this.handleSsoGoogle(dispatch, redirectAfterLogin);
    } else if (ssoQueryParam && ssoQueryParam === 'azure' && (!token || jwtService.hasExpired(token))) {
      const redirectUrl = removeQueryParam(window.location.origin + redirectAfterLogin, SSO_WITH_PARAM);
      dispatch(AccountActions.loginAzure(redirectUrl));
    } else if (location.search) {
      let tokenParam = getParameterByName(AUTH_TOKEN_PARAM, location.search);
      let refreshTokenParam = getParameterByName(REFRESH_TOKEN_PARAM, location.search);
      let urlToLogoutCallbackParam = getParameterByName(URL_TO_LOGOUT_CALLBACK, location.search);
      setUrlToLogoutCallbackInStorage(urlToLogoutCallbackParam);
      if (tokenParam) {
        authTokenStorageHandler(tokenParam);
        if (refreshTokenParam) {
          refreshTokenStorageHandler(refreshTokenParam);
        }

        let redirectUrl;
        if (isElectronApp()) {
          const parsedUrl = url.parse(url.format(location));
          const query = qs.parse(parsedUrl.query);
          delete query[AUTH_TOKEN_PARAM];
          delete query[REFRESH_TOKEN_PARAM];
          delete query[URL_TO_LOGOUT_CALLBACK];

          redirectUrl = url.format({
            pathname: parsedUrl.pathname,
            search: '?' + qs.stringify(query),
            hash: parsedUrl.hash
          });
        } else {
          redirectUrl = removeQueryParam(window.location.href, AUTH_TOKEN_PARAM);
          redirectUrl = removeQueryParam(redirectUrl, REFRESH_TOKEN_PARAM);
          redirectUrl = removeQueryParam(redirectUrl, URL_TO_LOGOUT_CALLBACK);
        }

        customRedirect(redirectUrl);
      } else {
        next();
      }
    } else if (token && token.length) {
      next();
    } else if (getGuestTokenFromStorage() && !jwtService.hasExpired(getGuestTokenFromStorage())) {
      // No hay sesión de usuario real, pero sí una sesión de invitado vigente: llevarlo a su catálogo
      // en vez de a login. La barrera de seguridad real es del backend (403 en rutas no permitidas).
      customRedirect(ROUTES.GUEST_CATALOG);
    } else {
      this.handleNotSignedIn(dispatch, redirectAfterLogin);
    }
  }

  checkValidClassroomId(nextState, replace, next) {
    const classroomId = nextState && nextState.params && nextState.params.idClassroom;

    if (!classroomId || 
        classroomId === 'undefined' || 
        classroomId === 'null' || 
        classroomId.trim() === '' ||
        classroomId.length < 10) {
      routerHistory.push({ pathname: ROUTES.HOME, state: { alertError: 'api.error.classroom.notFound' } });
    } else {
      next();
    }
  }

  redirectToLogin() {
    if (isElectronApp()) {
      customRedirect(ROUTES.LOGINAUTH);
    } else {
      window.location.replace(ROUTES.LOGINAUTH);
    }
  }

  handleNotSignedIn(dispatch, redirectAfterLogin) {
    dispatch(AccountActions.setRedirectAfterLogin(redirectAfterLogin));
    this.redirectToLogin();
  }

  handleSsoGoogle(dispatch, redirectAfterLogin) {
    dispatch(AccountActions.setRedirectAfterLogin(redirectAfterLogin));
    const route = `${ROUTES.LOGINAUTH}?ssoWith=google`;
    customRedirect(route);
  }

  redirectIfLoggedIn(nextState, replace, next) {
    const token = getTokenFromStorage();
    if (token && token.length !== 0) {
      customRedirect(ROUTES.ROOT);
    } else {
      let { dispatch } = this.props.store;
      dispatch(SectionActions.setDefault());
      if (isElectronApp()) {
        fetch(ROUTES.LOGINAUTH);
      } else if (![ROUTES.LOGINAUTH, ROUTES.RESET_PASSWORD].includes(routerHistory.getCurrentLocation().pathname)) {
        this.redirectToLogin();
      }
      next();
    }
  }

  setStudentRole(nextState, replace, next) {
    let { dispatch } = this.props.store;
    dispatch(setSignUpRole(ROLE.STUDENT));
    next();
  }

  setInstructorRole(nextState, replace, next) {
    let { dispatch } = this.props.store;
    dispatch(setSignUpRole(ROLE.INSTRUCTOR));
    next();
  }

  checkInstructorRole(nextState, replace, next) {
    const state = this.props.store.getState();
    const classroomId = nextState.params.idClassroom;
    const classroomRole = findClassroomRole(
      classroomId,
      state.classrooms.activeClassrooms,
      state.classrooms.selectedClassroom.classroom
    );
    const role = classroomRole || jwtService.getUserRole();

    if (role === ROLE.INSTRUCTOR || role === ROLE.COINSTRUCTOR) {
      next();
    } else {
      routerHistory.goBack();
    }
  }

  checkStudentRole(nextState, replace, next) {
    const state = this.props.store.getState();
    const classroomId = nextState.params.idClassroom;
    const classroomRole = findClassroomRole(
      classroomId,
      state.classrooms.activeClassrooms,
      state.classrooms.selectedClassroom.classroom
    );
    const role = classroomRole || jwtService.getUserRole();

    if (role === ROLE.STUDENT) {
      next();
    } else {
      routerHistory.goBack();
    }
  }

  checkEmailSentAndRedirectIfLoggedIn(nextState, replace, next) {
    const { store } = this.props;
    let state = store.getState();

    this.redirectIfLoggedIn.bind(this);
    if (state.account.newPasswordSent) {
      next();
    } else {
      routerHistory.push(ROUTES.LOGIN);
    }
  }

  render() {
    const { store } = this.props;

    return (
      <Provider store={store} className="app-docker">
        <Router history={routerHistory} className="app-docker">
          <Route path={ROUTES.ROOT} component={AppWrapper} onEnter={this.retrieveParameters}>
            <Route path="home" component={props => <Home {...props} tab={'MY_CLASSROOMS'} />} />
            <Route path="tasks" component={props => <Home {...props} tab={'ASSIGNMENTS'} />} />
            <Route path="library" component={props => <Home {...props} tab={'CLASSROOM_LIBRARY'} />} />
          <Route
            path="classroom/:idClassroom"
            component={props => <Classroom {...props} tab={'MY_CONTENTS'} />}
            onEnter={this.checkValidClassroomId}
          />
            <Route
              path="classroom/:idClassroom/analytics"
              component={props => <Classroom {...props} tab={'ANALYTICS'} />}
            onEnter={this.checkInstructorRole}
            onChange={this.checkValidClassroomId}
            />
            <Route
              path="classroom/:idClassroom/students"
              component={props => <Classroom {...props} tab={'CLASSROOM_STUDENTS'} />}
            onEnter={this.checkInstructorRole}
            onChange={this.checkValidClassroomId}
            />
          <Route
            path="classroom/:idClassroom/book/:idBook"
            component={BookContent}
            onEnter={this.checkValidClassroomId}
          />
            <Route
              path="classroom/:idClassroom/library"
            component={props => <Classroom {...props} tab={'CLASSROOM_LIBRARY'} />}
            onEnter={this.checkValidClassroomId}
            />
          <Route
            path="classroom/:idClassroom/book/:idBook/library"
            component={LibraryContainer}
            onEnter={this.checkValidClassroomId}
          />
            <Route
              path="classroom/:idClassroom/discussions"
            component={props => <Classroom {...props} tab={'DISCUSSIONS'} />}
            onEnter={this.checkValidClassroomId}
            />
          <Route
            path="classroom/:idClassroom/discussions/:idDiscussion"
            component={DiscussionPage}
            onEnter={this.checkValidClassroomId}
          />
            <Route
              path="classroom/:idClassroom/tasks"
            component={props => <Classroom {...props} tab={'ASSIGNMENTS'} />}
            onEnter={this.checkValidClassroomId}
            />
            <Route
              path="classroom/:idClassroom/gradebook"
            component={props => <Classroom {...props} tab={'GRADEBOOK'} />}
            onEnter={this.checkValidClassroomId}
            />
            <Route
              path="classroom/:idClassroom/annotations"
            component={props => <Classroom {...props} tab={'STUDENT_ANNOTATIONS'} />}
            onEnter={(nextState, replace, next) => {
              this.checkValidClassroomId(nextState, replace, () => {
                this.checkStudentRole(nextState, replace, next);
              });
            }}
            onChange={this.checkValidClassroomId}
            />
            <Route path="library" component={LibraryContainer} />
            <Route path="archive" component={ClassroomArchiveContainer} />
            <Route path="search" component={SearchResultsWrapper} />
            <Route path="launch" component={LaunchRedirect} />
          </Route>

          <Route path={ROUTES.LOGIN} component={AuthWrapper} onEnter={this.redirectIfLoggedIn}>
            <IndexRedirect to="auth" />
            <Route path="auth" component={Login} />
            <Route path="sent" component={Mail} onEnter={this.checkEmailSentAndRedirectIfLoggedIn} />
            <Route path="forget" component={Forget} />
            <Route path="sure" component={Sure} />
            <Route path="resetPassword" component={ResetPassword} />
          </Route>

          <Route path={ROUTES.JOIN} component={AuthWrapper} className="app-docker">
            <IndexRedirect to="index" />
            <Route path="index" component={Register} className="app-docker" />
            <Route path="google/index" component={RegisterGoogleCodeSelection} className="app-docker" />
            <Route path="azure/index" component={RegisterAzureCodeSelection} className="app-docker" />
          </Route>

          <Route path="close-window" component={CloseWindowPage} />
          <Route path="cookies" component={CookieDetails} />
          <Route path="g/:code" component={GuestLanding} />
          <Route path="guest/catalog" component={GuestCatalogPage} />
          <Route path={ROUTES.ERROR} component={ErrorPage} />
          <Route path="*" component={ErrorPage} />
        </Router>
      </Provider>
    );
  }
}
